import { GridViewMetadata } from '../../../grid/gridView/types';
import { ColumnDetails, ColumnType, ColumnValue } from '../../../grid/types/common';
import { SelectOption } from '../../../ui/gzSelect/types';
import { PackagesGridDataProvider } from '../dataProvider/PackagesGridDataProvider';
import { PackagesWebComponentSchema, ColumnKeys, Column, PackageType, PackageTypeData } from '../types';
import { Filter } from '../../../grid/gridFilters/types';
import { renderDescription, renderLanguageName, renderPackageName, renderPackageTypeName } from './customGridRenderers';
import { html } from 'lit';
import { Feature } from '@nirvana/shared/constants';
import { Dependencies } from '../../types/dependencies';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { CompanySpecialValues } from '@nirvana/shared/dtos/types/shared/filters';
import { getViewState } from '../../../utils/states.utils';
import { translateFn } from '../../../utils/translator.helper';

export class GridConfig {
  constructor(public dispatcher: any) {}

  private columnRenderers = {
    [ColumnKeys.PACKAGE_NAME]: renderPackageName.bind(this, this.dispatcher.packageNameClickEvent),
    [ColumnKeys.PACKAGE_TYPE]: renderPackageTypeName.bind(this),
    [ColumnKeys.COMPANY]: rowData => {
      return html` <gz-grid-cell-default-renderer
        .label="${rowData?.item?.company?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.LANGUAGE]: renderLanguageName.bind(this),
    [ColumnKeys.DESCRIPTION]: renderDescription.bind(this),
  };

  private pageSizeOptions = [
    {
      title: '20',
      value: '20',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  private defaultCompactViewChecked = false;
  private defaultPageSize = 100;

  private dependencies: Dependencies;
  private stringsTranslator: BaseWebComponentTranslator;

  public getDataForView(
    dependencies: Dependencies,
    stringsTranslator: BaseWebComponentTranslator
  ): PackagesWebComponentSchema {
    this.dependencies = dependencies;
    this.stringsTranslator = stringsTranslator;
    const dataProvider = new PackagesGridDataProvider(dependencies, this.getColumnDetails());
    const gridState = getViewState(dataProvider.viewStateName);

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'packages-grid-default-view',
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: gridState.columns ?? this.getColumns(),
      filters: gridState.filters ?? this.getDefaultFilters(),
      pageSize: gridState.pageSize ?? 20,
      isCompactViewChecked: gridState.compactView ?? false,
      hasSelectableRows: true,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      showGlobalActions: true,
      allColumnDetails: this.getColumnDetails(),
      pageNumber: 1,
      dataProvider: dataProvider,
      enableCSVExport: false,
      cellClassNameGenerator: this.getCellClassNameGenerator,
      columnConfigs: [],
      filtersUiConfigs: [],
    };

    return {
      viewMetaData,
      defaultViewProps: {
        displayedColumns: this.getColumns(),
        filters: this.getDefaultFilters(),
        pageSize: this.defaultPageSize,
        compactView: this.defaultCompactViewChecked,
      },
    };
  }

  private getDefaultFilters(): Array<Filter<ColumnKeys>> {
    let defaultFilters: Array<Filter<ColumnKeys>> = [
      {
        [ColumnKeys.PACKAGE_NAME]: { contains: '' },
      },
      {
        [ColumnKeys.DESCRIPTION]: { contains: '' },
      },
      { [ColumnKeys.PACKAGE_TYPE]: { in: [] } },
    ];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      defaultFilters = [
        {
          [ColumnKeys.COMPANY]: {
            in: [CompanySpecialValues.ALL],
          },
        },
        ...defaultFilters,
      ];
    }

    return defaultFilters;
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    let keys = [ColumnKeys.PACKAGE_NAME, ColumnKeys.PACKAGE_TYPE, ColumnKeys.DESCRIPTION];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      keys = [ColumnKeys.COMPANY, ...keys];
    }

    return keys;
  }

  private getColumns(): Column[] {
    return [Column.PACKAGE_NAME, Column.PACKAGE_TYPE, Column.LANGUAGE, Column.DESCRIPTION, Column.COMPANY];
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columnDetails = [
      {
        column: Column.PACKAGE_NAME,
        key: ColumnKeys.PACKAGE_NAME,
        columnName: translateFn('Name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 2,
      },
      {
        column: Column.PACKAGE_TYPE,
        key: ColumnKeys.PACKAGE_TYPE,
        columnName: translateFn('Type'),
        type: ColumnType.ENUM,
        values: this.getPackageTypeColumnValues(),
        removable: true,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 1,
      },
      {
        column: Column.LANGUAGE,
        key: ColumnKeys.LANGUAGE,
        columnName: translateFn('Language'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
        filteringDisabled: true,
        flexGrow: 1,
      },
      {
        column: Column.DESCRIPTION,
        key: ColumnKeys.DESCRIPTION,
        columnName: translateFn('Description'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 1,
      },
    ];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      const columnToInsert = {
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.COMPANY,
        removable: false,
        sortable: true,
        filteringDisabled: false,
        filterResetValue: [CompanySpecialValues.ALL],
        flexGrow: 1,
      };

      columnDetails.push(columnToInsert);
    }

    return columnDetails;
  }

  private getCellClassNameGenerator(_, rowData): string {
    const {
      item: { isGhost },
    } = rowData;

    return isGhost ? 'cell-ghost' : '';
  }

  private getPackageTypeColumnValues(): ColumnValue[] {
    const packageTypesData: PackageTypeData[] = [];

    const packageTypesNames: string[] = this.getPackageTypesNames();

    packageTypesData.push({ name: packageTypesNames[PackageType.Endpoints], value: PackageType.Endpoints });

    if (this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING)) {
      packageTypesData.push({ name: packageTypesNames[PackageType.SVA], value: PackageType.SVA });
    }

    if (this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_SANDBOX_LOCAL_VISIBLE)) {
      packageTypesData.push({ name: packageTypesNames[PackageType.Sandbox], value: PackageType.Sandbox });
    }

    if (this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_GHOSTR_VISIBLE)) {
      packageTypesData.push({ name: packageTypesNames[PackageType.Ghostr], value: PackageType.Ghostr });
    }

    if (
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_NETWORK_SENSORS) ||
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_NETWORK_SENSORS_USABLE_DELEGATE)
    ) {
      packageTypesData.push({ name: packageTypesNames[PackageType.NSVA], value: PackageType.NSVA });
    }

    if (
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_MANAGER) ||
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_VISIBLE)
    ) {
      packageTypesData.push(
        { name: packageTypesNames[PackageType.Frat], value: PackageType.Frat },
        { name: packageTypesNames[PackageType.RelayForFrat], value: PackageType.RelayForFrat }
      );
    }

    if (this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_KSPM_USABLE)) {
      packageTypesData.push({ name: packageTypesNames[PackageType.K8s], value: PackageType.K8s });
    }

    return packageTypesData.map(packageType => {
      return { label: packageTypesNames[packageType.value], value: packageType.value };
    });
  }

  private getPackageTypesNames(): string[] {
    const packageTypesNames: string[] = [];
    packageTypesNames[PackageType.SVA] = translateFn('Security Server');
    packageTypesNames[PackageType.Endpoints] = translateFn('BEST');
    packageTypesNames[PackageType.Sandbox] = translateFn('Security Appliance');
    packageTypesNames[PackageType.Ghostr] = translateFn('Network Sensor');
    packageTypesNames[PackageType.Frat] = translateFn('FRAT');
    packageTypesNames[PackageType.RelayForFrat] = this.dependencies.translateFn('FRAT with Relay');
    packageTypesNames[PackageType.NSVA] = translateFn('XDR NSVA');
    packageTypesNames[PackageType.K8s] = translateFn('Kubernetes');

    return packageTypesNames;
  }

  public handleStatusNA(columnValue: string): string {
    if (columnValue === '') {
      return this.dependencies.translateFn('N/A');
    }
    return columnValue;
  }
}
